/**
 * Lumia hareket katmanı — Adım 05 (14 Ağu 2026)
 *
 * apple-design ilkelerinin vanilla JS karşılığı. Yeni kütüphane YOK.
 * Sayfada zaten 200+ JS dosyası var; bu dosya tek başına ~6 KB.
 *
 *   1) Sepet drawer  — 1:1 sürükleme, rubber-band, momentum projeksiyonu,
 *                      hız devirli spring, hareket ortasında yakalanabilir
 *   2) Yapışkan CTA  — hero geçilince alttan girer (IntersectionObserver)
 *
 * Yalnızca `transform` ve `opacity` animasyonlanır — layout thrash yok.
 * prefers-reduced-motion: reduce  ->  hiçbiri kurulmaz.
 */

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

/* --------------------------------------------------------------------- *
 * Fizik yardımcıları
 * --------------------------------------------------------------------- */

/**
 * Momentum projeksiyonu — parmağın gittiği yeri tahmin eder.
 * Apple'ın "Designing Fluid Interfaces" örnek kodundaki üstel sönüm biçimi;
 * ders kitabındaki v²/(2a) DEĞİL.
 * @param {number} velocity px/s
 * @param {number} decelerationRate 0.998 = normal scroll hissi
 */
const project = (velocity, decelerationRate = 0.998) =>
  ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);

/**
 * Rubber-band — sınırın ötesine gidildikçe artan direnç.
 * Sert duruş "donmuş", artan direnç "burada başka bir şey yok" hissi verir.
 */
const rubberband = (overshoot, dimension, constant = 0.55) =>
  (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

/**
 * Spring — sönümleme oranı + tepki süresiyle tanımlanır (kütle/sertlik/sönüm üçlüsü değil).
 * Sabit süreli bir animasyon değil: süre parametrelerden doğar, ve her an
 * yeni bir hedefe/hıza yeniden yönlendirilebilir.
 *
 * @param {object} o
 * @param {number} o.from     başlangıç değeri (DAİMA ekrandaki canlı değer)
 * @param {number} o.to       hedef
 * @param {number} [o.velocity] px/s — jestten devralınan hız
 * @param {number} [o.damping]  1.0 = kritik sönüm (taşma yok), 0.8 = hafif yaylanma
 * @param {number} [o.response] saniye — hedefe ne kadar çabuk varır
 * @param {(v:number)=>void} o.onUpdate
 * @param {()=>void} [o.onDone]
 */
function spring({ from, to, velocity = 0, damping = 0.8, response = 0.3, onUpdate, onDone }) {
  const w0 = (2 * Math.PI) / response;
  let x = from - to; // hedefe göre yer değiştirme
  let v = velocity;
  let last = null;
  let raf = 0;
  let alive = true;

  const step = (now) => {
    if (!alive) return;
    if (last === null) last = now;
    // Kare atlarsa (sekme arkaplana gidip gelirse) fizik patlamasın diye tavan
    const dt = Math.min((now - last) / 1000, 1 / 30);
    last = now;

    // Yarı-implicit Euler, 240 Hz alt adımla — 60 Hz'de de kararlı
    const sub = Math.max(1, Math.ceil(dt * 240));
    const h = dt / sub;
    for (let i = 0; i < sub; i++) {
      const a = -w0 * w0 * x - 2 * damping * w0 * v;
      v += a * h;
      x += v * h;
    }

    onUpdate(to + x);

    if (Math.abs(x) < 0.5 && Math.abs(v) < 20) {
      onUpdate(to);
      alive = false;
      onDone?.();
      return;
    }
    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);

  return {
    stop() {
      alive = false;
      cancelAnimationFrame(raf);
    },
    get value() {
      return to + x;
    },
    get velocity() {
      return v;
    },
  };
}

/** Son ~100 ms'lik pointer geçmişinden hız (px/s). Tek noktadan hız güvenilmez. */
function velocityFrom(history) {
  if (history.length < 2) return 0;
  const last = history[history.length - 1];
  let ref = history[0];
  for (let i = history.length - 1; i >= 0; i--) {
    if (last.t - history[i].t > 100) break;
    ref = history[i];
  }
  const dt = last.t - ref.t;
  return dt > 0 ? ((last.x - ref.x) / dt) * 1000 : 0;
}

/* --------------------------------------------------------------------- *
 * 1) Sepet drawer — sürüklenerek kapatma
 * --------------------------------------------------------------------- */

/**
 * Sürüklemenin BAŞLAYAMAYACAĞI yerler — yalnızca yatay sürüklemesi kendi anlamı
 * olan alanlar. Link ve düğmeleri dışarıda bırakmak yanlıştı: dolu bir sepette
 * yüzeyin çoğu link/düğme olduğu için sürükleme neredeyse hiç devreye girmiyordu
 * (QA'da yakalandı). Bunun yerine her yerden takip edilir, niyet yatay çıkınca
 * devralınır ve ardından gelen click yutulur — apple-design §10.
 */
const NON_DRAG = 'input, select, textarea, [contenteditable]';

function enhanceCartDrawer() {
  const host = document.querySelector('cart-drawer-component');
  const panel = host?.querySelector('.cart-drawer__dialog');
  if (!host || !panel || panel.dataset.lkDrag === '1') return;
  panel.dataset.lkDrag = '1';

  let anim = null; // çalışan spring
  let dragging = false;
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let grabOffset = 0; // sürükleme başladığındaki canlı x — tutulan noktayı korur
  let history = [];
  let decided = false; // yatay mı dikey mi kararı verildi mi

  const width = () => panel.getBoundingClientRect().width || 380;

  const paint = (x) => {
    panel.style.transform = x === 0 ? '' : `translate3d(${x}px, 0, 0)`;
    // Zemin karartması sürüklemeyle birlikte azalır — malzeme hissi
    panel.style.setProperty('--lk-drawer-progress', String(1 - Math.min(1, Math.max(0, x / width()))));
  };

  const liveX = () => {
    if (anim) return anim.value;
    const m = new DOMMatrixReadOnly(getComputedStyle(panel).transform);
    return m.m41 || 0;
  };

  const settle = (to, velocity, onDone) => {
    anim?.stop();
    anim = spring({
      from: liveX(),
      to,
      velocity,
      // Jest momentum taşıdığı için hafif yaylanma hak edilmiş (skill: drawer 0.8 / 0.3)
      damping: 0.8,
      response: 0.3,
      onUpdate: paint,
      onDone: () => {
        anim = null;
        onDone?.();
      },
    });
  };

  const reset = () => {
    anim?.stop();
    anim = null;
    panel.style.transform = '';
    panel.style.removeProperty('--lk-drawer-progress');
    panel.classList.remove('lk-dragging');
  };

  panel.addEventListener(
    'pointerdown',
    (e) => {
      if (!panel.open || e.button !== 0 || e.target.closest(NON_DRAG)) return;
      // Hareket ortasında yakalama: koşan spring'i durdur, ekrandaki değerden devam et
      const current = liveX();
      anim?.stop();
      anim = null;

      dragging = false;
      decided = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      grabOffset = current;
      history = [{ x: e.clientX, t: e.timeStamp }];
    },
    { passive: true }
  );

  panel.addEventListener('pointermove', (e) => {
    if (pointerId !== e.pointerId) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (!decided) {
      // Tüm makul jestleri paralel dinle, niyet netleşince kaybedenleri iptal et
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      decided = true;
      if (Math.abs(dx) < Math.abs(dy) * 1.2) return; // dikey: listeye scroll bırak
      dragging = true;
      panel.setPointerCapture(e.pointerId);
      panel.classList.add('lk-dragging');
    }
    if (!dragging) return;

    e.preventDefault();
    history.push({ x: e.clientX, t: e.timeStamp });
    if (history.length > 12) history.shift();

    let x = grabOffset + dx;
    if (x < 0) x = -rubberband(-x, width()); // sola çekiş: yumuşak direnç
    paint(x);
  });

  const end = (e) => {
    if (pointerId !== e.pointerId) return;
    pointerId = null;
    if (!dragging) return;
    dragging = false;
    panel.classList.remove('lk-dragging');

    // Sürükleme bir linkin/düğmenin üstünde başlamış olabilir; bırakınca o link
    // tetiklenmesin. Niyet netleşti, kaybeden jesti iptal ediyoruz (apple-design §10).
    // Tıklama gelmezse dinleyici bir sonraki tıklamayı yutmasın diye kısa sürede sökülür.
    const swallow = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => removeEventListener('click', swallow, { capture: true }), 350);

    const v = velocityFrom(history);
    const x = liveX();
    // Karar konuma değil, jestin GİTTİĞİ yere göre verilir
    const projected = x + project(v);

    if (projected > width() * 0.35) {
      // Tema kendi kapanış animasyonunu çalıştırmasın; spring bitirsin
      panel.style.setProperty('--dialog-drawer-closing-animation', 'none');
      settle(width(), v, () => {
        host.close?.();
        // Bir sonraki açılış normal animasyonuyla gelsin
        requestAnimationFrame(() => {
          panel.style.removeProperty('--dialog-drawer-closing-animation');
          reset();
        });
      });
    } else {
      settle(0, v);
    }
  };

  panel.addEventListener('pointerup', end);
  panel.addEventListener('pointercancel', end);
  // Tema kapatırsa (Esc, kapat düğmesi, morph) kalıntı transform kalmasın
  panel.addEventListener('close', reset);
}

/* --------------------------------------------------------------------- *
 * 2) Yapışkan CTA — hero geçilince alttan girer
 * --------------------------------------------------------------------- */

function enhanceStickyCta() {
  const bar = document.querySelector('[data-lk-sticky-cta]');
  if (!bar || bar.dataset.lkInit === '1') return;
  const anchor = document.querySelector('[id*="hero"]') || document.querySelector('.hero');
  if (!anchor) return;
  bar.dataset.lkInit = '1';

  // scroll dinleyicisi yok: sürekli reflow yaratır ve mobilde kare düşürür
  new IntersectionObserver(
    ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
    { threshold: 0, rootMargin: '0px' }
  ).observe(anchor);
}

/* --------------------------------------------------------------------- *
 * Kurulum
 * --------------------------------------------------------------------- */

function init() {
  // Yapışkan CTA hareket değil, görünürlük kararıdır — azaltılmış harekette de
  // kurulur, yalnızca girişi CSS'te kaydırma yerine çapraz geçişe döner.
  enhanceStickyCta();
  if (reduceMotion.matches) return;
  enhanceCartDrawer();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Tema editöründe bölüm yeniden render olunca yeniden bağla
document.addEventListener('shopify:section:load', init);
